import { text } from "../utils/i18n";
import { safeObjectAssign } from "../utils/object";
import { privateUserId } from "../utils/user";
import { CallCohearingSession } from "./CallCohearingSession";
import { CallDisconnectReason } from "./CallDisconnectReason";
import { CallSummaryNode } from "./CallSummaryNode";
import { CallTranscriptStatus } from "./CallTranscriptNode";
import Comment from "./Comment";
import { LeadListMode } from "./LeadListMode";
import UploadedFile from "./UploadedFile";

/**
 * Represents a dialed call.
 */
export default class Call {
  readonly id: string;
  readonly serialId?: number;
  readonly date: Date;
  readonly recipient: string;
  readonly caller: string;
  readonly userId: string;
  readonly isInbound = false;
  readonly dialGroupId?: string;
  readonly shuffleDialGroupId?: string;
  readonly dialGroupMode?: LeadListMode;
  readonly audioFile?: UploadedFile;

  /**
   * May be null if the call did not get answered.
   */
  readonly connectedAt?: Date;
  readonly connectingAt: Date;
  readonly disconnectedAt: Date;
  readonly disconnectReason?: CallDisconnectReason;
  readonly disconnectCode?: string;

  /**
   * ID of the associated contact.
   */
  readonly contactId?: string;
  readonly comments: Comment[] = [];
  readonly tagIds: string[] = [];

  /**
   * For unrouted predictive calls only. Timestamp of when the contact answered the call.
   */
  readonly contactConnectedAt?: Date;
  readonly easyTelecomUserCallId?: string;
  readonly requestedQuarantine?: {
    releaseAt: Date;
    isCallbackAnyone: boolean;
  };
  readonly projectId?: string;
  readonly cohearingSessions: CallCohearingSession[] = [];
  readonly summary?: CallSummaryNode[];
  readonly transcriptStatus?: CallTranscriptStatus;

  constructor(deriveFrom?: Partial<Call>) {
    if (deriveFrom) {
      safeObjectAssign(this, deriveFrom);
    }

    if (this.date) {
      this.date = new Date(this.date);
    }

    if (this.comments) {
      this.comments = this.comments.map(comment => new Comment(comment));
    }

    if (this.connectedAt) {
      this.connectedAt = new Date(this.connectedAt);
    }

    if (this.connectingAt) {
      this.connectingAt = new Date(this.connectingAt);
    }

    if (this.disconnectedAt) {
      this.disconnectedAt = new Date(this.disconnectedAt);
    }

    if (this.requestedQuarantine) {
      this.requestedQuarantine.releaseAt = new Date(this.requestedQuarantine.releaseAt);      
    }

    this.summary = this.summary?.map(node => new CallSummaryNode(node));
  }

  get type(): CallType {
    if (this.userId !== privateUserId) {
      return null;
    }

    return this.contactConnectedAt ? CallType.UnroutedConnected : CallType.UnroutedNotConnected;
  }

  get isConnected() {
    return Boolean(this.connectedAt && !this.disconnectedAt);
  }

  get isDisconnected() {
    return Boolean((this.connectedAt || this.connectingAt) && this.disconnectedAt);
  }
}

export enum CallType {
  /**
   * Indicates that the call were never routed to a user and the contact did not answer.
   */
  UnroutedNotConnected = 'unroutedNotConnected',

  /**
   * Indicates that the call were never routed to a user but the contact did answer.
   */
  UnroutedConnected = 'unroutedConnected'
}

type CallDisconnectMetadata = {
  short: string;
  header?: string;
  long: string;

  /**
   * Determines whether to view the `long` string in header.
   */
  isHeaderComplete?: boolean;

  /**
   * Whether to draw more attention to the header.
   */
  isWarning?: boolean;
};

export function getCallDisconnectInfo(reason: CallDisconnectReason, code?: string): CallDisconnectMetadata {
  /*
   * From biztel dev:
   * no_route_destination/number_changed/no_route_transit_net -- Tyder på att numret inte är aktivt längre
   * subscriber_absent -- Telefon avstängd/ej nåbar (ingen voicemail aktiverad)
   * originator_cancel -- Inkommande samtal, inringande gav upp efter 45 sekunder utan svar
   * recovery_on_timer_expire -- Avslutat på vår sida efter 15s (den timeout ni angett via api)
   */

  if (reason === CallDisconnectReason.Other) {
    if (code === "no_route_destination" || code === "number_changed" || code === "no_route_transit_net") {
      return {
        short: text`Inaktivt nummer`,
        header: text`Numret är inaktivt`,
        long: text`Det uppringda numret är inte längre aktivt.`,
        isWarning: true
      };
    }

    if (code === "normal_temporary_failure" || code === "destination_out_of_order" ||
      code === "exchange_routing_error" || code === "unknown" || code === "bearercapability_notauth") {
      return {
        short: text`Tillfälligt fel`,
        header: text`Tillfälligt fel`,
        long: text`Felkod: ${code}`,
        isWarning: true
      };
    }
  }

  const translations: { [key in string]: CallDisconnectMetadata } = {
    [CallDisconnectReason.ContactHangUp]: {
      short: text`Kontakt lade på`,
      long: text`Kontakten lade på samtalet.`
    },
    [CallDisconnectReason.HeartbeatTimedOut]: {
      short: text`Anslutningen bröts`,
      header: text`Anslutningen bröts`,
      long: text`Samtalet lades på automatiskt eftersom säljaren inte var kopplad till samtalet.`,
      isWarning: true
    },
    [CallDisconnectReason.UserAnswerInboundCall]: {
      short: text`Säljare svarar annat samtal`,
      long: text`Säljaren svarade ett annat, ingående samtal och lade på detta samtalet.`
    },
    [CallDisconnectReason.UserHangUpNormal]: {
      short: text`Säljare lade på`,
      long: text`Säljaren lade på samtalet genom att klicka på lägg på-knappen.`
    },
    [CallDisconnectReason.UserHangUpCohearing]: {
      short: text`Medlyssnare lade på`,
      long: text`Medlyssnare lade på samtalet genom att klicka på lägg på-knappen.`
    },
    [CallDisconnectReason.UserOther]: {
      short: text`Säljare, övrigt`,
      long: text`Säljaren lade på samtalet automatiskt. Kontakta SaleSys kundservice för mer information.`
    },
    [CallDisconnectReason.CallAction]: {
      short: text`Samtalsarbete`,
      header: text`Ett samtalsarbete lade på samtalet`,
      long: text`Ett samtalsarbete lade på samtalet.`,
      isHeaderComplete: true
    },
    [CallDisconnectReason.Voicemail]: {
      short: text`Röstbrevlåda`,
      header: text`Samtalet nådde röstbrevlåda`,
      long: text`Samtalet nådde röstbrevlåda och lades på automatiskt.`
    },
    [CallDisconnectReason.UserDistributedOffer]: {
      short: text`Skickat avtal`,
      header: text`Skickat avtal`,
      long: text`Samtalet lades på automatiskt eftersom säljaren skickade ett avtal.`
    },
    [CallDisconnectReason.NoPredictiveModeUsers]: {
      short: text`Prediktivt läge avslutades`,
      header: text`Prediktivt läge avslutades`,
      long: text`Samtalet lades på automatiskt eftersom listan inte har en användare som ringer prediktivt.`
    },
    "subscriber_absent": {
      short: text`Ej nåbar`,
      header: text`Mobil avstängd eller ej nåbar`,
      long: text`Mobilen är avstängd eller på annat sätt ej nåbar, och röstbrevlåda finns inte.`,
      isWarning: true
    },
    "originator_cancel": {
      short: text`Inget svar`,
      header: text`Inget svar`,
      long: text`Samtalet besvarades inte.`
    },
    "recovery_on_timer_expire": {
      short: text`Inget svar`,
      header: text`Inget svar`,
      long: text`Samtalet besvarades inte.`
    },
    "unallocated_number": {
      short: text`Numret ej i bruk`,
      header: text`Numret ej i bruk`,
      long: text`Numret ej i bruk.`,
      isHeaderComplete: true,
      isWarning: true
    },
    "user_busy": {
      short: text`Upptagen`,
      header: text`Mottagaren är upptagen`,
      long: text`Mottagaren är upptagen eller nekade samtalet.`, // This error code occurs when declining a call, so it does not mean that the recipient is in another call.
    },
    "invalid_number_format": {
      short: text`Ogiltigt nummerformat`,
      header: text`Ogiltigt nummerformat`,
      long: text`Ogiltigt nummerformat. Kontrollera att numret är giltigt.`,
      isWarning: true
    },
  };

  const reasonAndCode = code ? `${reason} / ${code}` : reason;

  return translations[reason === CallDisconnectReason.Other ? code : reason] ||
  {
    short: text`Övrigt`,
    header: text`Samtalet avslutades med felkod`,
    long: `${text`Samtalet avslutades med felkod`}: ${reasonAndCode}`
  };
}